import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, EmptyState } from "@/components/Common";
import { Clock, CheckCircle2, XCircle, Plane, DollarSign, Users as UsersIcon, Trash2, Pencil, Loader2, Calendar as CalendarIcon, Plus, ChevronLeft, ChevronRight, RefreshCw, ShoppingCart, Package as PackageIcon, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${(parseFloat(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtHours = (h) => `${Math.floor(h)}h ${String(Math.round((h - Math.floor(h)) * 60)).padStart(2, "0")}m`;
const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

function LiveTab() {
  const [entries, setEntries] = useState([]);
  useEffect(() => {
    const load = () => api.get("/timeclock/entries").then((r) => setEntries(r.data)).catch(() => {});
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);
  const openShifts = entries.filter((e) => e.event_type === "shift" && !e.clock_out);
  const openLunches = new Set(entries.filter((e) => e.event_type === "lunch" && !e.clock_out).map((e) => e.staff_id));

  return openShifts.length === 0 ? (
    <Card className="border-slate-200"><EmptyState icon={Clock} title="Nobody is clocked in right now" hint="Employees clock in at /clockin using their PIN." /></Card>
  ) : (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {openShifts.map((e) => {
        const mins = Math.floor((Date.now() - new Date(e.clock_in)) / 60000);
        const h = Math.floor(mins / 60), m = mins % 60;
        return (
          <Card key={e.id} data-testid={`live-shift-${e.id}`} className="p-5 border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-heading font-semibold text-slate-800">{e.staff_name}</div>
                <div className="text-xs text-slate-400">since {new Date(e.clock_in).toLocaleTimeString()}</div>
              </div>
              <span className={`text-[11px] px-2 py-0.5 rounded-full ${openLunches.has(e.staff_id) ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                {openLunches.has(e.staff_id) ? "On lunch" : "Active"}
              </span>
            </div>
            <div className="font-mono text-2xl font-bold text-pink-800">{h}h {String(m).padStart(2, "0")}m</div>
          </Card>
        );
      })}
    </div>
  );
}

function TimecardsTab() {
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [staffFilter, setStaffFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [exporting, setExporting] = useState(null);

  const today = new Date();
  const monday = new Date(today); monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
  const [start, setStart] = useState(monday.toISOString().slice(0, 10));
  const [end, setEnd] = useState(sunday.toISOString().slice(0, 10));

  const load = useCallback(() => {
    api.get(`/timeclock/summary?start=${start}&end=${end}`).then((r) => setSummary(r.data)).catch(() => {});
    const q = staffFilter ? `&staff_id=${staffFilter}` : "";
    api.get(`/timeclock/entries?start=${start}&end=${end}${q}`).then((r) => setEntries(r.data)).catch(() => {});
    api.get("/timeclock/staff").then((r) => setStaffList(r.data)).catch(() => {});
  }, [start, end, staffFilter]);
  useEffect(() => { load(); }, [load]);

  const doExport = async (format, filename) => {
    setExporting(format);
    try {
      const path = format === "pdf" ? "/timeclock/summary/export.pdf"
                 : format === "gusto" ? "/timeclock/summary/export.gusto.csv"
                 : format === "zip" ? "/timeclock/timecards/bulk.zip"
                 : "/timeclock/summary/export";
      const res = await api.get(`${path}?start=${start}&end=${end}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch { toast.error("Export failed"); }
    finally { setExporting(null); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this punch?")) return;
    try { await api.delete(`/timeclock/entries/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4 border-slate-200">
        <div className="flex flex-col md:flex-row gap-3 md:items-end">
          <div><Label className="text-xs">Start</Label><Input data-testid="tc-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} /></div>
          <div><Label className="text-xs">End</Label><Input data-testid="tc-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
          <div className="flex-1"><Label className="text-xs">Employee</Label>
            <select data-testid="tc-staff-filter" value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)}
              className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm">
              <option value="">All employees</option>
              {staffList.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>
      </Card>

      {summary && summary.rows.length > 0 && (
        <Card className="p-0 border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
            <DollarSign className="w-4 h-4 text-pink-700" />
            <span className="font-heading font-semibold text-slate-800">Weekly Payroll Summary</span>
            <div className="ml-auto flex gap-2 flex-wrap">
              <Button data-testid="export-payroll-pdf" size="sm" variant="outline" onClick={() => doExport("pdf", `vera-payroll-${start}-${end}.pdf`)} disabled={!!exporting} className="h-8 gap-1.5">
                {exporting === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span>📄</span>} PDF
              </Button>
              <Button data-testid="export-payroll-gusto" size="sm" variant="outline" onClick={() => doExport("gusto", `gusto-payroll-${start}-${end}.csv`)} disabled={!!exporting} className="h-8 gap-1.5 border-pink-300 text-pink-800 hover:bg-pink-50">
                {exporting === "gusto" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span>💸</span>} Gusto CSV
              </Button>
              <Button data-testid="export-payroll-csv" size="sm" variant="outline" onClick={() => doExport("csv", `vera-payroll-${start}-${end}.csv`)} disabled={!!exporting} className="h-8 gap-1.5">
                {exporting === "csv" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span>📊</span>} Universal CSV
              </Button>
              <Button data-testid="export-timecards-zip" size="sm" variant="outline" onClick={() => doExport("zip", `vera-timecards-${start}-${end}.zip`)} disabled={!!exporting} className="h-8 gap-1.5 border-pink-300 text-pink-800 hover:bg-pink-50">
                {exporting === "zip" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span>🗂️</span>} Bulk Timecards ZIP
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table data-testid="payroll-summary">
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead>Employee</TableHead><TableHead>Rate</TableHead><TableHead>Net</TableHead>
                <TableHead>Reg</TableHead><TableHead>OT</TableHead><TableHead className="text-right">Gross</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {summary.rows.map((r) => (
                  <TableRow key={r.staff_id}>
                    <TableCell className="font-medium">{r.staff_name}</TableCell>
                    <TableCell className="font-mono text-sm">{money(r.hourly_rate)}/hr</TableCell>
                    <TableCell className="font-mono">{fmtHours(r.net_hours)}</TableCell>
                    <TableCell className="font-mono">{fmtHours(r.regular_hours)}</TableCell>
                    <TableCell className="font-mono text-amber-700">{fmtHours(r.overtime_hours)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-pink-800">{money(r.gross_pay)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Card className="p-0 border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <span className="font-heading font-semibold text-slate-800 flex items-center gap-2"><Clock className="w-4 h-4 text-pink-700" /> Punches</span>
          <Button data-testid="add-punch-btn" size="sm" onClick={() => setAdding(true)}
            className="bg-pink-700 hover:bg-pink-800 h-8 gap-1.5"><Plus className="w-3.5 h-3.5" /> Add Punch</Button>
        </div>
        {entries.length === 0 ? <EmptyState icon={Clock} title="No punches in this range" /> : (
          <div className="overflow-x-auto">
            <Table data-testid="entries-table">
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead>Employee</TableHead><TableHead>Type</TableHead><TableHead>Clock In</TableHead><TableHead>Clock Out</TableHead><TableHead>Hours</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {entries.map((e) => {
                  const hrs = e.clock_out ? (new Date(e.clock_out) - new Date(e.clock_in)) / 3600000 : null;
                  return (
                    <TableRow key={e.id} className="hover:bg-slate-50/60">
                      <TableCell className="font-medium">
                        {e.staff_name}
                        {e.edited_by && (
                          <div data-testid={`edit-trail-${e.id}`} className="text-[10px] text-amber-700 mt-0.5 flex items-center gap-1">
                            <Pencil className="w-2.5 h-2.5" />
                            edited by {e.edited_by} on {new Date(e.edited_at || e.updated_at || e.created_at).toLocaleDateString([], { month: "short", day: "numeric" })}
                          </div>
                        )}
                      </TableCell>
                      <TableCell><span className={`text-xs px-2 py-0.5 rounded ${e.event_type === "shift" ? "bg-pink-50 text-pink-800" : "bg-amber-50 text-amber-700"}`}>{e.event_type}</span></TableCell>
                      <TableCell className="font-mono text-xs">{fmtDateTime(e.clock_in)}</TableCell>
                      <TableCell className="font-mono text-xs">{e.clock_out ? fmtDateTime(e.clock_out) : <span className="text-emerald-600">Active</span>}</TableCell>
                      <TableCell className="font-mono">{hrs !== null ? fmtHours(hrs) : "—"}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button data-testid={`edit-${e.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(e)}><Pencil className="w-4 h-4 text-slate-500" /></Button>
                        <Button data-testid={`del-${e.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => del(e.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <EditEntryDialog entry={editing} onClose={() => setEditing(null)} onSaved={load} />
      <AddEntryDialog open={adding} onClose={() => setAdding(false)} onSaved={load} staffList={staffList} />
    </div>
  );
}

function EditEntryDialog({ entry, onClose, onSaved }) {
  const [event, setEvent] = useState("shift");
  const [ci, setCi] = useState("");
  const [co, setCo] = useState("");
  useEffect(() => {
    if (entry) {
      setEvent(entry.event_type || "shift");
      setCi(entry.clock_in ? entry.clock_in.slice(0, 16) : "");
      setCo(entry.clock_out ? entry.clock_out.slice(0, 16) : "");
    }
  }, [entry]);
  const save = async () => {
    try {
      await api.put(`/timeclock/entries/${entry.id}`, {
        event_type: event,
        clock_in: new Date(ci).toISOString(),
        clock_out: co ? new Date(co).toISOString() : null,
      });
      toast.success("Updated"); onClose(); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <Dialog open={!!entry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-entry-dialog">
        <DialogHeader><DialogTitle>Edit Punch — {entry?.staff_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Type</Label>
            <select data-testid="edit-entry-type" value={event} onChange={(e) => setEvent(e.target.value)}
              className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
              <option value="shift">Shift (Clock In / Out)</option>
              <option value="lunch">Lunch (Lunch In / Out)</option>
            </select>
          </div>
          <div><Label className="text-xs">Clock In</Label><Input data-testid="edit-entry-in" type="datetime-local" value={ci} onChange={(e) => setCi(e.target.value)} /></div>
          <div><Label className="text-xs">Clock Out</Label><Input data-testid="edit-entry-out" type="datetime-local" value={co} onChange={(e) => setCo(e.target.value)} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button data-testid="edit-entry-save" onClick={save} className="bg-pink-700 hover:bg-pink-800">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddEntryDialog({ open, onClose, onSaved, staffList }) {
  const [staffId, setStaffId] = useState("");
  const [event, setEvent] = useState("shift");
  const [ci, setCi] = useState("");
  const [co, setCo] = useState("");
  useEffect(() => {
    if (open) {
      setStaffId(""); setEvent("shift");
      const now = new Date();
      const iso = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      setCi(iso); setCo("");
    }
  }, [open]);
  const save = async () => {
    if (!staffId) { toast.error("Pick an employee"); return; }
    if (!ci) { toast.error("Clock-in time is required"); return; }
    try {
      await api.post("/timeclock/entries", {
        staff_id: staffId, event_type: event,
        clock_in: new Date(ci).toISOString(),
        clock_out: co ? new Date(co).toISOString() : null,
      });
      toast.success("Punch added"); onClose(); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-entry-dialog">
        <DialogHeader><DialogTitle>Add Punch</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Employee *</Label>
            <select data-testid="add-entry-staff" value={staffId} onChange={(e) => setStaffId(e.target.value)}
              className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
              <option value="">Select an employee…</option>
              {staffList.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select></div>
          <div><Label className="text-xs">Type</Label>
            <select data-testid="add-entry-type" value={event} onChange={(e) => setEvent(e.target.value)}
              className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
              <option value="shift">Shift (Clock In / Out)</option>
              <option value="lunch">Lunch (Lunch In / Out)</option>
            </select></div>
          <div><Label className="text-xs">Clock In *</Label><Input data-testid="add-entry-in" type="datetime-local" value={ci} onChange={(e) => setCi(e.target.value)} /></div>
          <div><Label className="text-xs">Clock Out (leave empty for open)</Label><Input data-testid="add-entry-out" type="datetime-local" value={co} onChange={(e) => setCo(e.target.value)} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button data-testid="add-entry-save" onClick={save} className="bg-pink-700 hover:bg-pink-800">Add Punch</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PtoApprovalsTab() {
  const [reqs, setReqs] = useState([]);
  const [denyReq, setDenyReq] = useState(null);
  const [denyReason, setDenyReason] = useState("");

  const load = useCallback(() => api.get("/timeclock/pto-requests").then((r) => setReqs(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const approve = async (id, override = false) => {
    try {
      await api.post(`/timeclock/pto-requests/${id}/approve${override ? "?override=true" : ""}`);
      toast.success(override ? "Approved (override — 2nd tech off)" : "Approved");
      load();
    } catch (e) {
      const msg = e.response?.data?.detail || "Approve failed";
      // If the backend blocked because another tech is already off, offer an admin
      // override — user asked for 1 tech per day with an admin-only bypass.
      if (msg.toLowerCase().includes("already approved off") ||
          msg.toLowerCase().includes("second technician")) {
        if (window.confirm(`${msg}\n\nOverride and approve a second technician anyway?`)) {
          return approve(id, true);
        }
      }
      toast.error(msg);
    }
  };

  const deny = async () => {
    if (!denyReason.trim()) { toast.error("Reason required to deny"); return; }
    try {
      await api.post(`/timeclock/pto-requests/${denyReq.id}/deny`, { admin_reason: denyReason });
      toast.success("Denied"); setDenyReq(null); setDenyReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Deny failed"); }
  };

  return (
    <div>
      {reqs.length === 0 ? (
        <Card className="border-slate-200"><EmptyState icon={Plane} title="No PTO requests" /></Card>
      ) : (
        <Card className="p-0 border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <Table data-testid="pto-requests-table">
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead>Employee</TableHead><TableHead>Dates</TableHead><TableHead>Hours</TableHead>
                <TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {reqs.map((r) => (
                  <TableRow key={r.id} data-testid={`pto-row-${r.id}`}>
                    <TableCell className="font-medium">{r.staff_name}</TableCell>
                    <TableCell className="font-mono text-xs">{r.start_date}{r.end_date !== r.start_date ? ` → ${r.end_date}` : ""}</TableCell>
                    <TableCell className="font-mono">{r.hours}h</TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-[240px]">
                      <div>{r.reason || "—"}</div>
                      {r.admin_reason && <div className="text-xs text-rose-600 mt-1">Manager: {r.admin_reason}</div>}
                    </TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1
                        ${r.status === "approved" ? "bg-emerald-100 text-emerald-800" : r.status === "denied" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>
                        {r.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {r.status === "pending" && (
                        <>
                          <Button data-testid={`approve-${r.id}`} size="sm" onClick={() => approve(r.id)} className="mr-2 bg-emerald-600 hover:bg-emerald-700 h-8 gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Approve</Button>
                          <Button data-testid={`deny-${r.id}`} size="sm" onClick={() => setDenyReq(r)} variant="outline" className="border-rose-300 text-rose-700 h-8 gap-1"><XCircle className="w-3.5 h-3.5" /> Deny</Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Dialog open={!!denyReq} onOpenChange={(o) => !o && setDenyReq(null)}>
        <DialogContent data-testid="deny-dialog">
          <DialogHeader><DialogTitle>Deny PTO — {denyReq?.staff_name}</DialogTitle></DialogHeader>
          <div>
            <Label className="text-xs">Reason (required, shown to employee)</Label>
            <Textarea data-testid="deny-reason" value={denyReason} onChange={(e) => setDenyReason(e.target.value)} rows={3} placeholder="e.g. Coverage conflict on requested dates" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDenyReq(null); setDenyReason(""); }}>Cancel</Button>
            <Button data-testid="confirm-deny" onClick={deny} className="bg-rose-600 hover:bg-rose-700">Deny request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function TimeClock() {
  return (
    <div>
      <PageHeader testid="timeclock-header" title="Time Clock" subtitle="Live shift board, weekly timecards + payroll, PTO approvals, and shared calendar events." />
      <Tabs defaultValue="live" className="w-full">
        <TabsList className="h-10 bg-slate-100 p-1">
          <TabsTrigger data-testid="tc-tab-live" value="live" className="gap-1.5"><Clock className="w-4 h-4" /> Live</TabsTrigger>
          <TabsTrigger data-testid="tc-tab-timecards" value="timecards" className="gap-1.5"><UsersIcon className="w-4 h-4" /> Timecards & Payroll</TabsTrigger>
          <TabsTrigger data-testid="tc-tab-pto" value="pto" className="gap-1.5"><Plane className="w-4 h-4" /> PTO Approvals</TabsTrigger>
          <TabsTrigger data-testid="tc-tab-events" value="events" className="gap-1.5"><CalendarIcon className="w-4 h-4" /> Events</TabsTrigger>
          <TabsTrigger data-testid="tc-tab-requests" value="requests" className="gap-1.5"><ShoppingCart className="w-4 h-4" /> Purchase Requests</TabsTrigger>
        </TabsList>
        <TabsContent value="live" className="mt-4"><LiveTab /></TabsContent>
        <TabsContent value="timecards" className="mt-4"><TimecardsTab /></TabsContent>
        <TabsContent value="pto" className="mt-4"><PtoApprovalsTab /></TabsContent>
        <TabsContent value="events" className="mt-4"><EventsTab /></TabsContent>
        <TabsContent value="requests" className="mt-4"><AdminPurchaseRequestsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function EventsTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const empty = { title: "", note: "", start_date: new Date().toISOString().slice(0, 10),
                   end_date: new Date().toISOString().slice(0, 10), all_day: true, color: "#0d9488",
                   recurrence: "none", recurrence_until: "" };
  const load = useCallback(() => api.get("/timeclock/events")
    .then((r) => setRows(r.data || [])).catch(() => toast.error("Load failed")), []);
  useEffect(() => { load(); }, [load]);

  const save = async (row) => {
    try {
      if (row.id) await api.put(`/timeclock/events/${row.id}`, row);
      else await api.post("/timeclock/events", row);
      toast.success(row.id ? "Event updated" : "Event added");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => {
    if (!confirm("Delete this event?")) return;
    try { await api.delete(`/timeclock/events/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button data-testid="event-new-btn" onClick={() => setEditing({ ...empty })}
          className="bg-pink-700 hover:bg-pink-800 gap-1.5">
          <Plus className="w-4 h-4" /> New Event
        </Button>
      </div>
      <Card className="border-slate-200">
        {rows.length === 0 ? (
          <div className="p-8 text-center text-slate-400 italic">No events scheduled.</div>
        ) : (
          <Table data-testid="events-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Title</TableHead>
                <TableHead>Start</TableHead>
                <TableHead>End</TableHead>
                <TableHead>Repeats</TableHead>
                <TableHead>Color</TableHead>
                <TableHead>Note</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`event-row-${r.id}`}>
                  <TableCell className="font-medium">{r.title}</TableCell>
                  <TableCell className="font-mono text-xs">{r.start_date}</TableCell>
                  <TableCell className="font-mono text-xs">{r.end_date || r.start_date}</TableCell>
                  <TableCell className="text-xs">
                    {r.recurrence && r.recurrence !== "none" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold uppercase">
                        <RefreshCw className="w-3 h-3" /> {r.recurrence}
                        {r.recurrence_until ? ` · until ${r.recurrence_until}` : ""}
                      </span>
                    ) : <span className="text-slate-400">One-time</span>}
                  </TableCell>
                  <TableCell>
                    <span className="inline-block w-6 h-4 rounded" style={{ backgroundColor: r.color }} />
                    <span className="text-[10px] ml-1 font-mono text-slate-500">{r.color}</span>
                  </TableCell>
                  <TableCell className="text-xs text-slate-600 max-w-xs truncate">{r.note}</TableCell>
                  <TableCell className="text-right">
                    <button onClick={() => setEditing(r)} data-testid={`event-edit-${r.id}`}
                      className="text-slate-500 hover:text-pink-800 mr-2"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(r.id)} data-testid={`event-del-${r.id}`}
                      className="text-slate-500 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
      {editing && <EventDialog row={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function EventDialog({ row, onClose, onSave }) {
  const [f, setF] = useState(row);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const recurring = f.recurrence && f.recurrence !== "none";
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{row.id ? "Edit Event" : "Add Event"}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div>
            <Label>Title *</Label>
            <Input data-testid="ev-title" value={f.title || ""} onChange={(e) => set("title", e.target.value)}
              className="h-9 text-sm" placeholder="Team Meeting / AC Repair / Closed" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Start date *</Label>
              <Input data-testid="ev-start" type="date" value={f.start_date}
                onChange={(e) => set("start_date", e.target.value)} className="h-9 text-sm" />
            </div>
            <div>
              <Label>End date</Label>
              <Input data-testid="ev-end" type="date" value={f.end_date}
                onChange={(e) => set("end_date", e.target.value)} className="h-9 text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Repeats</Label>
              <select data-testid="ev-recurrence" value={f.recurrence || "none"}
                onChange={(e) => set("recurrence", e.target.value)}
                className="w-full h-9 text-sm px-2 rounded-md border border-slate-200">
                <option value="none">Does not repeat</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Every 2 weeks</option>
                <option value="monthly">Monthly (same day)</option>
              </select>
            </div>
            {recurring && (
              <div>
                <Label>Repeat until (optional)</Label>
                <Input data-testid="ev-until" type="date" value={f.recurrence_until || ""}
                  onChange={(e) => set("recurrence_until", e.target.value)}
                  className="h-9 text-sm" />
              </div>
            )}
          </div>
          {recurring && (
            <div className="text-[11px] text-slate-500 bg-emerald-50 border border-emerald-200 rounded p-2">
              Auto-generates on the calendar every {f.recurrence === "weekly" ? "week" : f.recurrence === "biweekly" ? "2 weeks" : "month"}
              {f.recurrence_until ? ` until ${f.recurrence_until}` : " indefinitely"}. Edit the master here to change every instance.
            </div>
          )}
          <div>
            <Label>Color</Label>
            <div className="flex gap-2 items-center">
              <input type="color" value={f.color || "#0d9488"}
                onChange={(e) => set("color", e.target.value.toLowerCase())}
                data-testid="ev-color"
                className="h-9 w-16 rounded-md border border-slate-200" />
              <Input value={f.color || ""} onChange={(e) => set("color", e.target.value.toLowerCase())}
                className="h-9 text-sm font-mono w-32" />
            </div>
          </div>
          <div>
            <Label>Note</Label>
            <Textarea data-testid="ev-note" value={f.note || ""} onChange={(e) => set("note", e.target.value)}
              rows={2} className="text-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="ev-save" onClick={() => onSave(f)}
            className="bg-pink-700 hover:bg-pink-800">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const PR_STATUSES = ["pending", "approved", "ordered", "received", "denied"];
const PR_STATUS_STYLES = {
  pending:  "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  ordered:  "bg-sky-100 text-sky-800",
  received: "bg-slate-200 text-slate-700",
  denied:   "bg-rose-100 text-rose-800",
};

function AdminPurchaseRequestsTab() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/purchase-requests${q}`).then((r) => setRows(r.data || [])).catch(() => {});
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (row, status) => {
    try {
      await api.put(`/purchase-requests/${row.id}`, { status });
      toast.success(`Marked ${status}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try { await api.delete(`/purchase-requests/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const pending = rows.filter((r) => r.status === "pending").length;

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <select data-testid="pr-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
          className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm">
          <option value="all">All statuses</option>
          {PR_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {pending > 0 && filter === "all" && (
          <span className="text-xs text-amber-800 bg-amber-100 rounded-full px-2 py-0.5">
            {pending} pending
          </span>
        )}
      </div>

      {rows.length === 0 ? (
        <Card className="border-slate-200"><EmptyState icon={ShoppingCart} title="No purchase requests" hint="Employees can submit from /clockin → Requests." /></Card>
      ) : (
        <Card className="p-0 border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <Table data-testid="pr-admin-table">
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead>Employee</TableHead><TableHead>Item</TableHead>
                <TableHead>Category</TableHead><TableHead>Urg.</TableHead>
                <TableHead>Est.</TableHead><TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`pr-row-${r.id}`}>
                    <TableCell className="font-medium">{r.staff_name}</TableCell>
                    <TableCell className="max-w-[240px]">
                      <div className="text-sm text-slate-800 truncate">{r.item}
                        <span className="ml-1.5 text-xs font-mono text-slate-400">×{r.quantity}</span>
                      </div>
                      {r.vendor_hint && <div className="text-xs text-slate-400">Hint: {r.vendor_hint}</div>}
                      {r.notes && <div className="text-xs text-slate-500 italic mt-0.5 truncate">"{r.notes}"</div>}
                      {r.admin_note && <div className="text-xs text-emerald-700 mt-0.5"><b>Admin:</b> {r.admin_note}</div>}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">{r.category}</TableCell>
                    <TableCell><span className={`text-xs px-1.5 py-0.5 rounded ${r.urgency === "Urgent" || r.urgency === "High" ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-600"}`}>{r.urgency}</span></TableCell>
                    <TableCell className="font-mono text-xs">{r.estimated_cost > 0 ? money(r.estimated_cost) : "—"}</TableCell>
                    <TableCell><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${PR_STATUS_STYLES[r.status] || ""}`}>{r.status}</span></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {r.status === "pending" && (
                        <>
                          <Button data-testid={`pr-approve-${r.id}`} size="sm" onClick={() => setStatus(r, "approved")} className="mr-1 bg-emerald-600 hover:bg-emerald-700 h-8 gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Approve</Button>
                          <Button data-testid={`pr-deny-${r.id}`} size="sm" variant="outline" onClick={() => setEditing({ ...r, _action: "deny" })} className="mr-1 border-rose-300 text-rose-700 h-8 gap-1"><XCircle className="w-3.5 h-3.5" />Deny</Button>
                        </>
                      )}
                      {r.status === "approved" && (
                        <Button data-testid={`pr-ordered-${r.id}`} size="sm" onClick={() => setStatus(r, "ordered")} className="mr-1 bg-sky-600 hover:bg-sky-700 h-8 gap-1"><PackageIcon className="w-3.5 h-3.5" />Ordered</Button>
                      )}
                      {r.status === "ordered" && (
                        <Button data-testid={`pr-received-${r.id}`} size="sm" onClick={() => setStatus(r, "received")} className="mr-1 bg-slate-600 hover:bg-slate-700 h-8 gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Received</Button>
                      )}
                      <Button data-testid={`pr-note-${r.id}`} size="sm" variant="ghost" onClick={() => setEditing({ ...r, _action: "note" })} className="h-8"><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button data-testid={`pr-delete-${r.id}`} size="sm" variant="ghost" onClick={() => remove(r.id)} className="h-8 text-rose-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <AdminPurchaseRequestDialog editing={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}

function AdminPurchaseRequestDialog({ editing, onClose, onSaved }) {
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("pending");
  useEffect(() => {
    if (editing) {
      setNote(editing.admin_note || "");
      setStatus(editing._action === "deny" ? "denied" : (editing.status || "pending"));
    }
  }, [editing]);
  const save = async () => {
    try {
      await api.put(`/purchase-requests/${editing.id}`, { status, admin_note: note });
      toast.success("Saved"); onClose(); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  if (!editing) return null;
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pr-admin-dialog">
        <DialogHeader><DialogTitle className="font-heading">
          {editing._action === "deny" ? "Deny request" : "Update request"} · {editing.item}
        </DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-slate-500">
            {editing.staff_name} · {editing.category} · qty {editing.quantity}
          </div>
          <div><Label className="text-xs">Status</Label>
            <select data-testid="pr-status-select" value={status} onChange={(e) => setStatus(e.target.value)}
              className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
              {PR_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select></div>
          <div><Label className="text-xs">Admin note (visible to employee)</Label>
            <Textarea data-testid="pr-admin-note" rows={3} value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Ordering from McKesson today; ETA Thursday." /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="pr-admin-save" onClick={save} className="bg-pink-700 hover:bg-pink-800">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
